import { createBrowserRouter, redirect } from 'react-router-dom';
import type { ComponentType } from 'react';
import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom';
import { RouteError } from './RouteError.tsx';
import { tokenStorage } from '@infra/storage/token-storage.ts';
import { queryClient } from '@infra/api/query-client.ts';
import { useAuthStore } from '@features/auth/auth.store.ts';
import {
  companyActivitiesQuery,
  companyZonesQuery,
} from '@features/agro-dashboard/hooks/company-lists.queries.ts';

/** Redirect to /login if there is no valid access token. */
function requireAuth() {
  const hasToken = !!tokenStorage.getAccessToken() && !tokenStorage.isTokenExpired();
  if (!hasToken) return redirect('/login');
  return null;
}

/** Redirect to /dashboard if the user already has a valid active token. */
function loginLoader() {
  const hasToken = !!tokenStorage.getAccessToken() && !tokenStorage.isTokenExpired();
  if (hasToken) return redirect('/dashboard');
  return null;
}

/**
 * Define una ruta protegida con el componente en un chunk aparte.
 *
 * Antes, las 26 rutas se importaban de forma estática y acababan todas en el bundle
 * inicial: 2,35 MB que había que descargar y parsear antes de pintar el login. Con
 * `lazy` cada pantalla viaja en su propio chunk y sólo se descarga al visitarla.
 *
 * El guard de sesión se queda **estático** a propósito. React Router no permite un
 * `loader` estático y otro dentro de `lazy` a la vez, y tenerlo fuera significa que a
 * un usuario sin sesión se le redirige a /login sin descargar antes el chunk de una
 * página que no va a ver.
 */
function protectedRoute(
  path: string,
  load: () => Promise<{ Component: ComponentType }>,
): RouteObject {
  return {
    path,
    loader: requireAuth,
    lazy: load,
    errorElement: <RouteError />,
  };
}

export const router = createBrowserRouter([
  {
    path: '/login',
    loader: loginLoader,
    lazy: async () => ({ Component: (await import('./routes/login.route.tsx')).LoginRoute }),
    errorElement: <RouteError />,
  },
  {
    // Sin requireAuth a propósito: la navega Chromium headless (backend) con un token
    // de corta duración por query string, no una sesión interactiva — ver
    // VigilanciaDiarioRenderView.tsx.
    path: '/vigilancia-informe/render-diario',
    lazy: async () => ({ Component: (await import('./routes/vigilancia-informe-render.route.tsx')).VigilanciaInformeRenderRoute }),
    errorElement: <RouteError />,
  },
  {
    // La tabla en vivo necesita sus parámetros en la URL; se validan aquí, sin cargar
    // el chunk, porque la comprobación no depende de nada del módulo de la ruta.
    path: '/',
    loader: ({ request }: LoaderFunctionArgs) => {
      const authRedirect = requireAuth();
      if (authRedirect) return authRedirect;

      const search = new URL(request.url).searchParams;
      if (!search.get('sqlQuery') || !search.get('Id_company')) {
        return redirect('/dashboard');
      }
      return null;
    },
    lazy: async () => ({ Component: (await import('./routes/live-table.route.tsx')).LiveTableRoute }),
    errorElement: <RouteError />,
  },

  {
    // El loader arranca las dos listas mientras se descarga el chunk de la pantalla.
    // Antes no salían hasta que montaba el componente, y las tres consultas pesadas
    // esperaban a que terminaran: se pagaban en serie. No se hace `await` a propósito
    // — el objetivo es que las peticiones salgan ya, no retrasar la navegación.
    path: '/dashboard',
    loader: () => {
      const authRedirect = requireAuth();
      if (authRedirect) return authRedirect;

      const { company, user } = useAuthStore.getState();
      const companyId = Number(company?.id_company ?? user?.id_company ?? 0);
      if (companyId > 0) {
        void queryClient.prefetchQuery(companyActivitiesQuery(companyId));
        void queryClient.prefetchQuery(companyZonesQuery(companyId));
      }
      return null;
    },
    lazy: async () => ({ Component: (await import('./routes/dashboard.route.tsx')).DashboardRoute }),
    errorElement: <RouteError />,
  },
  protectedRoute('/dia-a-dia', async () => ({
    Component: (await import('./routes/dia-a-dia.route.tsx')).DiaADiaRoute,
  })),
  protectedRoute('/admin', async () => ({
    Component: (await import('./routes/admin.route.tsx')).AdminRoute,
  })),
  protectedRoute('/admin/zones', async () => ({
    Component: (await import('./routes/admin-zones.route.tsx')).AdminZonesRoute,
  })),
  protectedRoute('/admin/migracion', async () => ({
    Component: (await import('./routes/migration.route.tsx')).MigrationRoute,
  })),
  protectedRoute('/operadores', async () => ({
    Component: (await import('./routes/operators.route.tsx')).OperatorsRoute,
  })),
  protectedRoute('/dispositivos', async () => ({
    Component: (await import('./routes/devices.route.tsx')).DevicesRoute,
  })),
  protectedRoute('/actividades', async () => ({
    Component: (await import('./routes/activities.route.tsx')).ActivitiesRoute,
  })),
  protectedRoute('/actividades/nuevo', async () => ({
    Component: (await import('./routes/activity-builder.route.tsx')).ActivityBuilderRoute,
  })),
  protectedRoute('/actividades/:id/editar', async () => ({
    Component: (await import('./routes/activity-builder.route.tsx')).ActivityBuilderRoute,
  })),
  protectedRoute('/usuarios-portal', async () => ({
    Component: (await import('./routes/portal-users.route.tsx')).PortalUsersRoute,
  })),
  protectedRoute('/consolidados', async () => ({
    Component: (await import('./routes/consolidados.route.tsx')).ConsolidadosRoute,
  })),
  protectedRoute('/ciclos', async () => ({
    Component: (await import('./routes/ciclos.route.tsx')).CiclosRoute,
  })),
  protectedRoute('/produccion', async () => ({
    Component: (await import('./routes/produccion.route.tsx')).ProduccionRoute,
  })),
  protectedRoute('/metricas', async () => ({
    Component: (await import('./routes/metricas.route.tsx')).MetricasRoute,
  })),
  protectedRoute('/cerebro', async () => ({
    Component: (await import('./routes/brain.route.tsx')).BrainRoute,
  })),
  protectedRoute('/asistente', async () => ({
    Component: (await import('./routes/asistente.route.tsx')).AsistenteRoute,
  })),
  protectedRoute('/sanidad', async () => ({
    Component: (await import('./routes/sanidad.route.tsx')).SanidadRoute,
  })),
  protectedRoute('/vigilancia', async () => ({
    Component: (await import('./routes/vigilancia.route.tsx')).VigilanciaRoute,
  })),
  protectedRoute('/vigilancia-informe', async () => ({
    Component: (await import('./routes/vigilancia-informe.route.tsx')).VigilanciaInformeRoute,
  })),
  protectedRoute('/trazabilidad', async () => ({
    Component: (await import('./routes/trazabilidad.route.tsx')).TrazabilidadRoute,
  })),
  protectedRoute('/pesos-lotes', async () => ({
    Component: (await import('./routes/headquarters-weights.route.tsx')).HeadquartersWeightsRoute,
  })),
  // Hub de Reportes (Admin → Reportes): Programados (envío automático por correo) vs.
  // Personalizados (visualización/gestión de reportes SQL) — dos features distintas detrás de
  // una sola entrada de navegación.
  protectedRoute('/reportes', async () => ({
    Component: (await import('./routes/reportes.route.tsx')).ReportesRoute,
  })),
  protectedRoute('/reportes-programados', async () => ({
    Component: (await import('./routes/reportes-programados.route.tsx')).ReportesProgramadosRoute,
  })),
  protectedRoute('/reportes-programados/:reportType', async () => ({
    Component: (await import('./routes/reportes-programados-detalle.route.tsx')).ReportesProgramadosDetalleRoute,
  })),
  protectedRoute('/reportes-personalizados', async () => ({
    Component: (await import('./routes/custom-reports.route.tsx')).CustomReportsRoute,
  })),
  protectedRoute('/documentacion', async () => ({
    Component: (await import('./routes/documentacion.route.tsx')).DocsHubRoute,
  })),
  {
    // Antes que '/documentacion/:slug' para que 'interna' no se tome por un slug.
    // Estas dos validan el slug contra un catálogo que vive en el módulo de la ruta,
    // así que su loader viaja dentro del chunk; la sesión se sigue comprobando ahí.
    path: '/documentacion/interna/:slug',
    lazy: async () => {
      const m = await import('./routes/documentacion-interna.route.tsx');
      return { loader: m.docsInternalGuideLoader, Component: m.DocsInternalGuideRoute };
    },
    errorElement: <RouteError />,
  },
  {
    path: '/documentacion/:slug',
    lazy: async () => {
      const m = await import('./routes/documentacion-guia.route.tsx');
      return { loader: m.docsGuideLoader, Component: m.DocsGuideRoute };
    },
    errorElement: <RouteError />,
  },
  {
    path: '*',
    loader: () => {
      const authRedirect = requireAuth();
      return authRedirect ?? redirect('/dashboard');
    },
    errorElement: <RouteError />,
  },
]);
